/**
 * useNotifications — manages notification state for the Notification Center.
 *
 * Phase 1 implementation: local state with mock data.
 * Phase 2+ will wire to `GET /users/notifications` via TanStack Query and
 * `system.notification` WebSocket events for real-time push.
 */
import { useCallback, useMemo, useState } from 'react';

export type NotificationType =
  | 'agent'
  | 'workflow'
  | 'task'
  | 'connection'
  | 'pipeline'
  | 'security'
  | 'memory'
  | 'system';

export interface NotificationAction {
  label: string;
  type: 'navigate' | 'action' | 'dismiss';
  route?: string;
  actionId?: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  timestamp: number;
  read: boolean;
  icon: string;
  color: string;
  actions: NotificationAction[];
  /** @deprecated Use actions array instead */
  actionLabel?: string;
  /** @deprecated Use actions array instead */
  actionRoute?: string;
}

export interface UseNotificationsReturn {
  notifications: readonly Notification[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clearAllRead: () => void;
  executeAction: (notificationId: string, action: NotificationAction) => void;
}

/**
 * Relative timestamp formatter.
 * Returns human-friendly strings like "2m ago", "1h ago", "Yesterday".
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Returns a time-based group label for notification grouping.
 */
export function getTimeGroup(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffHr = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHr / 24);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return 'This week';
}

const now = Date.now();

const MOCK_NOTIFICATIONS: readonly Notification[] = [
  {
    id: 'notif-001',
    type: 'agent',
    title: 'Developer finished CR review analysis',
    description: 'Completed static analysis of CR-274716206 with 3 findings and 2 suggestions for improvement.',
    timestamp: now - 2 * 60 * 1000, // 2m ago
    read: false,
    icon: 'CheckCircle',
    color: 'var(--wayne-green)',
    actions: [
      { label: 'Start a chat', type: 'navigate', route: '/play' },
      { label: 'View details', type: 'navigate', route: '/tasks' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-002',
    type: 'workflow',
    title: 'Daily CR Review workflow failed at step 3',
    description: 'The automated code review workflow encountered a timeout error during the security scan phase.',
    timestamp: now - 15 * 60 * 1000, // 15m ago
    read: false,
    icon: 'AlertTriangle',
    color: 'var(--wayne-red)',
    actions: [
      { label: 'Start a chat about this', type: 'navigate', route: '/play' },
      { label: 'View execution', type: 'navigate', route: '/workflows/execution/1' },
      { label: 'Retry', type: 'action', actionId: 'retry-workflow' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-003',
    type: 'task',
    title: 'New SIM ticket assigned: T-1234567',
    description: 'High-priority ticket regarding Bedrock AgentCore latency spike in us-west-2 production.',
    timestamp: now - 45 * 60 * 1000, // 45m ago
    read: false,
    icon: 'ListChecks',
    color: 'var(--agent-devops)',
    actions: [
      { label: 'Start a chat', type: 'navigate', route: '/play' },
      { label: 'Open in SIM', type: 'action', actionId: 'open-external' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-004',
    type: 'connection',
    title: 'Slack connection token expires in 2 hours',
    description: 'Your Slack integration OAuth token will expire soon. Reconnect to maintain real-time notifications.',
    timestamp: now - 1.5 * 60 * 60 * 1000, // 1.5h ago
    read: false,
    icon: 'AlertCircle',
    color: 'var(--wayne-orange)',
    actions: [
      { label: 'Reauthenticate', type: 'action', actionId: 'reauth' },
      { label: 'Resync now', type: 'action', actionId: 'resync' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-005',
    type: 'pipeline',
    title: 'WayneAIPlatform pipeline deployed to Beta',
    description: 'All 15 sub-targets deployed successfully. Integration tests passed with 0 failures.',
    timestamp: now - 3 * 60 * 60 * 1000, // 3h ago
    read: false,
    icon: 'GitBranch',
    color: 'var(--wayne-green)',
    actions: [
      { label: 'Start a chat', type: 'navigate', route: '/play' },
      { label: 'View pipeline', type: 'action', actionId: 'open-external' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-006',
    type: 'security',
    title: 'New medium-severity finding from AppSec scan',
    description: 'Token Cache Poisoning vulnerability detected. Terminated employee may retain access for up to 1 hour.',
    timestamp: now - 6 * 60 * 60 * 1000, // 6h ago
    read: true,
    icon: 'Shield',
    color: 'var(--wayne-red)',
    actions: [
      { label: 'Start a chat about this', type: 'navigate', route: '/play' },
      { label: 'View finding', type: 'action', actionId: 'open-external' },
      { label: 'Acknowledge', type: 'action', actionId: 'acknowledge' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-007',
    type: 'memory',
    title: 'Wayne learned 3 new facts from your last session',
    description: 'Added context about your preferred testing frameworks, deployment patterns, and code review style.',
    timestamp: now - 12 * 60 * 60 * 1000, // 12h ago
    read: true,
    icon: 'Brain',
    color: 'var(--agent-analyst)',
    actions: [
      { label: 'View memory', type: 'navigate', route: '/memory' },
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
  {
    id: 'notif-008',
    type: 'system',
    title: 'Scheduled maintenance window tomorrow 4-6 AM PT',
    description: 'Wayne AI Platform will undergo routine maintenance. Chat and agent services will be temporarily unavailable.',
    timestamp: now - 18 * 60 * 60 * 1000, // 18h ago
    read: true,
    icon: 'Info',
    color: 'var(--wayne-ink-3)',
    actions: [
      { label: 'Dismiss', type: 'dismiss' },
    ],
  },
] as const;

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>(
    // Deep copy so mutations don't touch the mock source
    () => MOCK_NOTIFICATIONS.map((n) => ({ ...n, actions: [...n.actions] })),
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  const markRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAllRead = useCallback(() => {
    setNotifications((prev) => prev.filter((n) => !n.read));
  }, []);

  const executeAction = useCallback((notificationId: string, action: NotificationAction) => {
    if (action.type === 'dismiss') {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      return;
    }

    if (action.type === 'action') {
      // Mark as read; real action handlers will be wired in Phase 2
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
      );
      return;
    }

    // type === 'navigate' — mark as read, navigation handled by the component
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
    );
  }, []);

  return { notifications, unreadCount, markRead, markAllRead, dismiss, clearAllRead, executeAction };
}
